import { map, Observable, Subject } from "rxjs";
import { MapAction, updateMap } from "./delta";
import { useEffect, useState } from "@lukekaalim/act";
import { Logger } from "./logging";

export type PredictorMapFuncs<Key, Value, UpdateEvent, Seq> = {
  flattenKey: (key: Key) => string,
  flattenSeq: (key: Seq) => string | number,
  read: (key: Key) => Value | null,
  write: (key: Key, value: Value) => void,
  apply: (event: MapAction<Key, Value, UpdateEvent>) => void,
  remove: (key: Key) => void,
}

export type PredictorOptions = {
  stableIds?: boolean,
  logger?: Logger
}

export class Predictor2<Key, Value, UpdateEvent, Seq = number> {
  predictionByKey: Map<string, Prediction2<Key, Value, UpdateEvent, Seq>> = new Map();
  predictionBySeq: Map<string | number, Prediction2<Key, Value, UpdateEvent, Seq>> = new Map();

  #mapFuncs: PredictorMapFuncs<Key, Value, UpdateEvent, Seq>;
  #options: PredictorOptions;

  log(message: string) {
    if (this.#options.logger)
      this.#options.logger.log(message)
  }
  error(message: string) {
    if (this.#options.logger)
      this.#options.logger.error(message)
  }

  constructor(
    mapFuncs: PredictorMapFuncs<Key, Value, UpdateEvent, Seq>,
    options: PredictorOptions = {}
  ) {
    this.#mapFuncs = mapFuncs;
    this.#options = options;
  }

  record(predictedEvent: MapAction<Key, Value, UpdateEvent>, actionSeq: Seq) {
    const existingPrediction = this.predictionByKey.get(this.#mapFuncs.flattenKey(predictedEvent.key));
    if (!existingPrediction) {
      this.log(`Starting new prediction for key ${predictedEvent.key}, sequence ${actionSeq}`)
      // Add a new prediction,
      // and return the predicted event accordingly
      const prediction: Prediction2<Key, Value, UpdateEvent, Seq> = {
        originalValue: this.#mapFuncs.read(predictedEvent.key) || null,
        events: [[actionSeq, predictedEvent]]
      };
      this.predictionByKey.set(this.#mapFuncs.flattenKey(predictedEvent.key), prediction);
      this.predictionBySeq.set(this.#mapFuncs.flattenSeq(actionSeq), prediction);
      this.#mapFuncs.apply(predictedEvent);
      return true;
    }
    if (!this.#options.stableIds && existingPrediction.events[0][1].type === 'add') {
      // Cannot predict any events after "add"
      this.log(`Can't predict any events after add`)
      return false;
    }
    this.log(`Extending existing prediction $for key ${predictedEvent.key}, sequence ${actionSeq}`)
    
    this.predictionBySeq.set(this.#mapFuncs.flattenSeq(actionSeq), existingPrediction);
    existingPrediction.events.push([actionSeq, predictedEvent]);
    this.#mapFuncs.apply(predictedEvent);
    return true;
  }

  reconcile(realEvent: MapAction<Key, Value, UpdateEvent>, eventSeq: Seq) {
    const feSeq = this.#mapFuncs.flattenSeq(eventSeq);
    const existingPrediction = this.predictionBySeq.get(feSeq);
    if (!existingPrediction) {
      // no prediction for event
      return false;
    }
    this.log(`Reconciling existing prediction for sequence (${eventSeq})`)

    for (let i = 0; i < existingPrediction.events.length; i++) {
      const [actionId, predictedEvent] = existingPrediction.events[i];
      const faSeq = this.#mapFuncs.flattenSeq(actionId);
      this.predictionBySeq.delete(feSeq);
      this.log(`Checking event[${i}] for matching actionId (${faSeq} === ${feSeq}) ${faSeq === feSeq}`)

      if (faSeq === feSeq) {
        this.log(`Match confirmed`)
        // restore state:
        if (realEvent.type !== 'remove')
          if (existingPrediction.originalValue)
            this.#mapFuncs.write(realEvent.key, existingPrediction.originalValue);

        // remove guess add
        if (!this.#options.stableIds && predictedEvent.type === 'add') {
          this.log(`Removing ADD guess for "${predictedEvent.key}" (real ID is "${realEvent.key}")`)
          this.#mapFuncs.remove(predictedEvent.key);
        }

        // perform real update
        this.#mapFuncs.apply(realEvent);
        existingPrediction.originalValue = this.#mapFuncs.read(realEvent.key);

        // Remove any "skipped" events, plus the predicted one
        existingPrediction.events = existingPrediction.events.slice(i + 1);
        this.log(`Remaining events: ${existingPrediction.events.map(e => e[0])}`)

        // cleanup
        if (existingPrediction.events.length === 0) {
          this.predictionByKey.delete(this.#mapFuncs.flattenKey(predictedEvent.key));
        }

        // run prediction reply
        for (const [, replayEvent] of existingPrediction.events) {
          this.#mapFuncs.apply(replayEvent);
        }
        return true;
      }
    }
    this.error(`Failed to find ${eventSeq} for key ${realEvent.key}`)
  }
}

export class SimpleMapPredictor<Key extends string, Value> extends Predictor2<Key, Value, Value, number> {

  constructor(
    storage: Map<Key, Value>,
    options: PredictorOptions = {}
  ) {
    super({
      flattenKey(key) {
        return key;
      },
      flattenSeq(key) {
        return key;
      },
      apply(event) {
        updateMap({
          add(key, value) {
            storage.set(key, value);
          },
          remove(key) {
            storage.delete(key);
          },
          update(key, value) {
            storage.set(key, value);
          },
        }, event)
      },
      read(key) {
        return storage.get(key) || null;
      },
      remove(key) {
        storage.delete(key);
      },
      write(key, value) {
        storage.set(key, value);
      },
    }, options)
  }
}

export type Prediction2<Key, Value, Event, Seq> = {
  originalValue: null | Value;
  events: [eventSeqId: Seq, MapAction<Key, Value, Event>][];
};

export class SubjectData<V> implements ClientData<V> {
  current: V;
  change: Subject<V>;

  constructor(initial: V) {
    this.current = initial;
    this.change = new Subject();
  }

  set(value: V) {
    this.current = value;
    this.notify();
  }

  notify() {
    this.change.next(this.current);
  }

  update(updater: (current: V) => V | void) {
    const next = updater(this.current);
    if (next)
      this.set(next)
    else
      this.notify();
  }
}

/**
 * A reference to a live-update value,
 * controlled by some client (with the goal
 * of synchronizing with a remote host).
 * 
 * You can subscribe to "change" to receive a notification
 * when the value is updated.
 */
export interface ClientData<V> {
  current: V;

  change: Observable<V>;
}

export type ClientMap<K, V> = {
  ids: ClientData<K[]>;

  get(key: K): null | ClientData<V>;
}

export type ClientMapState<K, V> = {
  map: ClientMap<K, V>,

  keys: K[],
  useValue(key: K): V
}

export const useClientData = <V>(data: ClientData<V>) => {
  const [value, setValue] = useState(() => data.current);

  useEffect(() => {
    setValue(data.current);
    const sub = data.change.subscribe(setValue);
    return () => sub.unsubscribe();
  }, [data])

  return value;
}

export const useMappedClientData = <V, X>(
  data: ClientData<V>,
  mapData: (value: V, prev: X) => X,
  defaultValue: X
) : X => {
  const [value, setValue] = useState(() => mapData(data.current, defaultValue));

  useEffect(() => {
    setValue(prev => mapData(data.current, prev));

    const sub = data.change.subscribe(value =>
      setValue(prev => mapData(value, prev)));
    return () => sub.unsubscribe();
  }, [data, map])

  return value;
}

export const useNullableMappedClientData = <V, X>(data: null | ClientData<V>, mapData: (value: V) => X): X | null => {
  const [value, setValue] = useState(() => data && mapData(data.current));

  useEffect(() => {
    if (!data)
      return;

    setValue(mapData(data.current));
    const sub = data.change.pipe(map(mapData)).subscribe(setValue);
    return () => sub.unsubscribe();
  }, [data, map])

  return value;
}
